import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import type { team_issueFieldTeamReadviewFull_TeamReadView$key as TeamFragment } from '@atlassian/jira-relay/src/__generated__/team_issueFieldTeamReadviewFull_TeamReadView.graphql';
import TeamProfileCard from '@atlassian/jira-profilecard-next/src/ui/team-profilecard/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { TeamReadViewWithGpp } from '@atlassian/jira-issue-field-team-readview-with-gpp/src/ui/team/index.tsx';

import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import {
	PRODUCT_DISCOVERY_PROJECT,
	SERVICE_DESK_PROJECT,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import type { TeamReadViewProps } from './types.tsx';

/**
 * The TeamReadView will show a read only view of Team field.
 * @param props [TeamReadViewProps](./types.tsx)
 */
export const TeamReadView = ({ fragmentRef, maxLines, triggerLinkType }: TeamReadViewProps) => {
	const { selectedTeam } = useFragment<TeamFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment team_issueFieldTeamReadviewFull_TeamReadView on JiraTeamViewField {
				selectedTeam {
					jiraSuppliedId
					jiraSuppliedName
				}
			}
		`,
		fragmentRef,
	);

	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);
	const isJSMOrJPDProject =
		projectType === SERVICE_DESK_PROJECT || projectType === PRODUCT_DISCOVERY_PROJECT;

	if (!selectedTeam?.jiraSuppliedName) {
		return <EmptyFieldText />;
	}

	if (
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jira-preview-panel-team-integration') &&
		fg('jira_preview_panel_m_1_rollout') &&
		fg('jira-glance-panel') &&
		(fg('jsm_enable_preview_panel') || !isJSMOrJPDProject)
	) {
		return (
			<TeamReadViewWithGpp
				teamName={selectedTeam?.jiraSuppliedName}
				ariId={selectedTeam?.jiraSuppliedId}
				maxLines={maxLines}
				showTeamPreview
			/>
		);
	}

	return (
		<TeamProfileCard
			teamId={selectedTeam.jiraSuppliedId}
			trigger="hover-click"
			position="left-start"
			triggerLinkType={triggerLinkType}
		>
			<Text maxLines={maxLines}>{selectedTeam.jiraSuppliedName}</Text>
		</TeamProfileCard>
	);
};
